import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import {DcpTechnicalInformationData} from '@app/model/dcp-technical-information-data';
import {DcpComponentClassifications, JsonService, TypeValuePair} from '@dcp/configurator';
import {NgbModal, NgbModalOptions} from '@ng-bootstrap/ng-bootstrap';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CmsComponent, Product} from '@spartacus/core';
import {CmsComponentData, CurrentProductService} from '@spartacus/storefront';
import {Observable} from 'rxjs';
import {DcpProductUtilitiesService} from '../../services/dcp-product-utilities.service';
import {ClassificationsTemplateComponent} from '../classifications-template/classifications-template.component';
import {NavigationLink} from '../navigation-bar/navigation-link';
import {ProductDetailsNavigationLinkService} from '../navigation-bar/product-details-navigation-link.service';
import {ProductDetailsSectionComponent} from '../product-details-section/product-details-section.component';

export interface ProductTechnicalInformationComponent extends CmsComponent {
  classificationsProductData?: DcpComponentClassifications[];
}

@UntilDestroy()
@Component({
  selector: 'dcp-technical-information',
  templateUrl: './technical-information.component.html',
  styleUrls: ['./technical-information.component.scss'],
})
export class TechnicalInformationComponent extends ProductDetailsSectionComponent
  implements OnInit, OnDestroy, AfterContentInit, AfterViewInit {

  @ViewChildren(ClassificationsTemplateComponent) classificationsTemplate!: QueryList<ClassificationsTemplateComponent>;
  product$: Observable<Product>;
  product: Product;
  componentData$: Observable<ProductTechnicalInformationComponent>;
  productClassifications: DcpComponentClassifications[];
  deployment: DcpTechnicalInformationData[] = [];
  operations: DcpTechnicalInformationData[] = [];
  security: DcpTechnicalInformationData[] = [];
  compliance: DcpTechnicalInformationData[] = [];
  globalization: DcpTechnicalInformationData[] = [];
  navLink: NavigationLink = {
    headline: 'productDetails.technicalInformation',
    id: 'technical-information-section'
  };

  translationsHeaders: string[];
  allTechnicalInformationData: DcpTechnicalInformationData[][];
  hasMethods: boolean[];
  totNumOfPanels = 0;

  constructor(
    dcpProductUtilitiesService: DcpProductUtilitiesService,
    protected currentProductService: CurrentProductService,
    protected changeDetection: ChangeDetectorRef,
    protected productDetailsNavigationLinkService: ProductDetailsNavigationLinkService,
    private cmsData: CmsComponentData<ProductTechnicalInformationComponent>,
    private modalService: NgbModal,
    private jsonService: JsonService,
  ) {
    super(currentProductService, changeDetection, productDetailsNavigationLinkService, dcpProductUtilitiesService);
  }

  get hasGeneral(): boolean {
    return !!this.product &&
      ((!!this.product.classifications && !!this.product.classifications.length) ||
        (!!this.product.supcategories && !!this.product.supcategories.length));
  }

  get hasDeployment(): boolean {
    return this.deployment && this.deployment.length > 0;
  }

  get hasOperations(): boolean {
    return this.operations && this.operations.length > 0;
  }

  get hasSecurity(): boolean {
    return this.security && this.security.length > 0;
  }

  get hasCompliance(): boolean {
    return this.compliance && this.compliance.length > 0;
  }

  get hasGlobalization(): boolean {
    return this.globalization && this.globalization.length > 0;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.componentData$ = this.cmsData.data$;
    this.fetchComponentData();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngAfterContentInit(): void {
    super.ngAfterContentInit();
  }

  ngAfterViewInit(): void {
    this.classificationsTemplate.changes.pipe(untilDestroyed(this)).subscribe(() => {
      this.changeDetection.detectChanges();
    });
  }

  fetchComponentData() {
    this.componentData$.pipe(untilDestroyed(this)).subscribe((componentData) => {
      if (componentData.classificationsProductData) {
        this.productClassifications =
          this.jsonService.parseSafely(componentData?.classificationsProductData.toString()) as DcpComponentClassifications[];
      }
    });
  }

  isVisible(): boolean {
    return this.hasGeneral || this.hasDeployment || this.hasOperations || this.hasSecurity
      || this.hasCompliance || this.hasGlobalization;
  }

  /**
   * Open a modal form
   *
   * @param content the modal window
   */
  openModal(content: any) {
    const modalOptions: NgbModalOptions = {centered: true};
    this.modalService.open(content, modalOptions);
  }

  /**
   * Supply all data needed by the template
   */
  prepareData(): void {
    if (this.product) {
      if (this.product.data) {
        this.prepareTechnicalInformation();
      }
      if (this.allTechnicalInformationData) {
        this.totNumOfPanels = this.allTechnicalInformationData.filter(i => i.length > 0).length;
      }
    }
  }

  /**
   * Data for the technical information sections
   */
  prepareTechnicalInformation() {
    this.deployment = [];
    this.operations = [];
    this.security = [];
    this.compliance = [];
    this.globalization = [];
    const rawTechnicalInformation: TypeValuePair = this.product?.data.find(entry => entry.type === 'selfDeclarations');
    if (!!rawTechnicalInformation && rawTechnicalInformation.value) {
      const parsedTechnicalInformationData: DcpTechnicalInformationData[] = this.jsonService.parseSafely(rawTechnicalInformation.value) as DcpTechnicalInformationData[];
      if (parsedTechnicalInformationData) {
        this.deployment = parsedTechnicalInformationData.filter(headline => headline.type.trim() === 'Deployment');
        this.operations = parsedTechnicalInformationData.filter(headline => headline.type.trim() === 'Operations');
        this.security = parsedTechnicalInformationData.filter(headline => headline.type.trim() === 'Security');
        this.compliance = parsedTechnicalInformationData.filter(headline => headline.type.trim() === 'Compliance');
        this.globalization = parsedTechnicalInformationData.filter(headline => headline.type.trim() === 'Globalization');
        this.prepareTemplate();
      }
    }
  }

  /**
   * Parse data for the component's template
   */
  prepareTemplate() {
    this.translationsHeaders = [
      'productDetails.deployment',
      'productDetails.operations',
      'productDetails.security',
      'productDetails.compliance',
      'productDetails.globalization'
    ];

    this.allTechnicalInformationData = [
      this.deployment,
      this.operations,
      this.security,
      this.compliance,
      this.globalization
    ];

    this.hasMethods = [
      this.hasDeployment,
      this.hasOperations,
      this.hasSecurity,
      this.hasCompliance,
      this.hasGlobalization
    ];
  }

  /**
   * Returns next highest integer
   *
   * @param x the number to cut in half
   */
  half(x: number) {
    return Math.ceil(x / 2);
  }
}
